import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { Tooltip } from 'primeng/tooltip';
import { CardSoftwareLibraryModule, LOGIN_AUTH_SERVICE, LOGIN_CONFIG, SierraTheme } from '@card-software/library';
import { errorInterceptor, SITE_API_BASE_URL, siteInterceptor } from '@card-software/nexus-utilities';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { authInterceptor } from './auth.interceptor';
import { BatchDyeingCycleModalComponent } from './components/batch/_modals/batch-dyeing-cycle-modal/batch-dyeing-cycle-modal.component';
import { CreateLotItemModalComponent } from './components/batch/_modals/create-lot-item-modal/create-lot-item-modal.component';
import { BatchDetailsComponent } from './components/batch/batch-details/batch-details.component';
import { BatchesComponent } from './components/batch/batches/batches.component';
import { ConsumptionVarianceComponent } from './components/batch/consumption-variance/consumption-variance.component';
import { CurrentShiftComponent } from './components/current-shift/current-shift.component';
import { DashboardProductionCardComponent } from './components/dashboard-production-cards/dashboard-production-card/dashboard-production-card.component';
import { CardMachineComponent } from './components/dyeing/dyeing-dashboard/card-machine/card-machine.component';
import { DyeingMachineDashboardComponent } from './components/dyeing/dyeing-dashboard/dyeing-machine-dashboard/dyeing-machine-dashboard.component';
import { DyeingMachineDetailsComponent } from './components/dyeing/dyeing-dashboard/dyeing-machine-details/dyeing-machine-details.component';
import { DyeingDetailsCardComponent } from './components/dyeing/dyeing-details-card/dyeing-details-card.component';
import { DyeingForecastViewerComponent } from './components/dyeing/dyeing-forecast-viewer/dyeing-forecast-viewer.component';
import { BalanceoModalComponent } from './components/finishing/_modals/balanceo-modal/balanceo-modal.component';
import { CuadrajeModalComponent } from './components/finishing/_modals/cuadraje-modal/cuadraje-modal.component';
import { FinishingCardComponent } from './components/finishing/finishing-card/finishing-card.component';
import { FinishingDashboardComponent } from './components/finishing/finishing-dashboard/finishing-dashboard.component';
import { FinishingDashboardDetailsComponent } from './components/finishing/finishing-dashboard-details/finishing-dashboard-details.component';
import { RollFinalQcComponent } from './components/finishing/roll-final-qc/roll-final-qc.component';
import { KnittingOrderComponent } from './components/knitting-order/knitting-order/knitting-order.component';
import { KnittingOrderBalanceComponent } from './components/knitting-order/knitting-order-balance/knitting-order-balance.component';
import { KnittingOrderDetailsComponent } from './components/knitting-order/knitting-order-details/knitting-order-details.component';
import { KnittingOrderFilterComponent } from './components/knitting-order/knitting-order-filter/knitting-order-filter.component';
import { LaboratoryTestsComponent } from './components/laboratory-tests/laboratory-tests/laboratory-tests.component';
import { DyehouseDailyMetricPreviewModalComponent } from './components/main/dyehouse-daily-metric/_modals/dyehouse-daily-metric-preview-modal/dyehouse-daily-metric-preview-modal.component';
import { DyehouseDailyMetricComponent } from './components/main/dyehouse-daily-metric/dyehouse-daily-metric.component';
import { MachineAssignmentPreviewModalComponent } from './components/main/machine-assignment/_modals/machine-assignment-preview-modal/machine-assignment-preview-modal.component';
import { MachineAssignmentComponent } from './components/main/machine-assignment/machine-assignment.component';
import { PlanningDashboardComponent } from './components/planning/planning-dashboard/planning-dashboard.component';
import { ProductionCardsDetailsComponent } from './components/planning/product-order/production-cards-details/production-cards-details.component';
import { WorkOrdersComponent } from './components/planning/product-order/work-orders/work-orders.component';
import { AddFromGreigeModalComponent } from './components/production-card/_modals/add-from-greige-modal/add-from-greige-modal.component';
import { ChangeDyeingMachineModalComponent } from './components/production-card/_modals/change-dyeing-machine-modal/change-dyeing-machine-modal.component';
import { CloseProductionCardModalComponent } from './components/production-card/_modals/close-production-card-modal/close-production-card-modal.component';
import { ConsumedItemModalComponent } from './components/production-card/_modals/consumed-item-modal/consumed-item-modal.component';
import { ReassignmentModalComponent } from './components/production-card/_modals/reassignment-modal/reassignment-modal.component';
import { ReorganizeProcessModalComponent } from './components/production-card/_modals/reorganize-process-modal/reorganize-process-modal.component';
import { ReturnRollsToGreigeModalComponent } from './components/production-card/_modals/return-rolls-to-greige-modal/return-rolls-to-greige-modal.component';
import { SecondQualityRollModalComponent } from './components/production-card/_modals/second-quality-roll-modal/second-quality-roll-modal.component';
import { CycleRecipeValuesComponent } from './components/production-card/cycle-recipe-values/cycle-recipe-values.component';
import { FinishingRouteDetailComponent } from './components/production-card/finishing-route-detail/finishing-route-detail.component';
import { IndividualProductionCardDetailsComponent } from './components/production-card/individual-production-card-details/individual-production-card-details.component';
import { IndividualProductionCardGeneralInformationComponent } from './components/production-card/individual-production-card-general-information/individual-production-card-general-information.component';
import { ProductionCardBundlesComponent } from './components/production-card/production-card-bundles/production-card-bundles.component';
import { ProductionCardMoveToBatchComponent } from './components/production-card/production-card-move-to-batch/production-card-move-to-batch.component';
import { ReassignRollsComponent } from './components/production-card/reassign-rolls/reassign-rolls.component';
import { ReprocessCreationComponent } from './components/production-card/reprocess/reprocess-creation/reprocess-creation.component';
import { FabricSummaryTableComponent } from './components/production-card/tables/fabric-summary-table/fabric-summary-table.component';
import { RollsDisplayTableComponent } from './components/production-card/tables/rolls-display-table/rolls-display-table.component';
import { ProductionWorkOrderDetailsComponent } from './components/production-work-order/production-work-order-details/production-work-order-details.component';
import { ProductionWorkOrdersComponent } from './components/production-work-order/production-work-orders/production-work-orders.component';
import { ProductionReportingComponent } from './components/reporting/production-reporting.component';
import { RollSplitComponent } from './components/roll-split/roll-split.component';
import { AssignRollsToPcComponent } from './components/shared/_modals/assign-rolls-to-pc/assign-rolls-to-pc.component';
import { CurrentShiftModalComponent } from './components/shared/_modals/current-shift-modal/current-shift-modal.component';
import { FabricInformationModalComponent } from './components/shared/_modals/fabric-information-modal/fabric-information-modal.component';
import { GreigeDetailModalComponent } from './components/shared/_modals/greige-detail-modal/greige-detail-modal.component';
import { ProductionCardsSelectionComponent } from './components/shared/_modals/production-cards-selection/production-cards-selection.component';
import { RelationKnittingsModalComponent } from './components/shared/_modals/relation-knittings-modal/relation-knittings-modal.component';
import { RollDetailModalComponent } from './components/shared/_modals/roll-detail-modal/roll-detail-modal.component';
import { WorkOrderQuantitiesModalComponent } from './components/shared/_modals/work-order-quantities-modal/work-order-quantities-modal.component';
import { CardCompositionComponent } from './components/shared/card-composition/card-composition.component';
import { ColorInfoComponent } from './components/shared/color-info/color-info.component';
import { ProductionCardComponent } from './components/shared/production-card/production-card.component';
import { ProductionCardDetailsComponent } from './components/shared/production-card-details/production-card-details.component';
import { ProductionCardsViewerComponent } from './components/shared/production-cards-viewer/production-cards-viewer.component';
import { SidebarKnittingProgrammingComponent } from './components/shared/sidebar-knitting-programming/sidebar-knitting-programming.component';
import { ViewStatusComponent } from './components/shared/view-status/view-status.component';
import { WorkOrderSummaryViewerComponent } from './components/shared/work-order-summary-viewer/work-order-summary-viewer.component';
import { KnittingDashboardComponent } from './components/stations/knitting-dashboard/knitting-dashboard.component';
import { MachinesAssignmentComponent } from './components/stations/machines-assignment/machines-assignment.component';
import { StationsComponent } from './components/stations/stations/stations.component';
import { WeightStationComponent } from './components/stations/weight/weight-station/weight-station.component';
import { WeightStationDetailComponent } from './components/stations/weight/weight-station-detail/weight-station-detail.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { GuatemalaTimePipe } from './pipes/guatemala-time.pipe';
import { TranslateLocalizedPipe } from './pipes/translate-localized.pipe';
import { TranslateWithFallbackPipe } from './pipes/translationWithFallback.pipe';
import { PrimengModule } from './primeng/primeng.module';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { UserAuthenticationService } from './services/user-authentication.service';

@NgModule({
  declarations: [
    AppComponent,
    AssignRollsToPcComponent,
    CardCompositionComponent,
    ColorInfoComponent,
    DyeingDetailsCardComponent,
    DyeingForecastViewerComponent,
    FabricInformationModalComponent,
    GreigeDetailModalComponent,
    KnittingDashboardComponent,
    KnittingOrderComponent,
    KnittingOrderDetailsComponent,
    MachinesAssignmentComponent,
    PlanningDashboardComponent,
    ProductionCardComponent,
    ProductionCardDetailsComponent,
    ProductionCardsDetailsComponent,
    ProductionCardsSelectionComponent,
    ProductionCardsViewerComponent,
    RelationKnittingsModalComponent,
    RollDetailModalComponent,
    SidebarKnittingProgrammingComponent,
    StationsComponent,
    ViewStatusComponent,
    WeightStationComponent,
    WeightStationDetailComponent,
    WorkOrdersComponent,
    CurrentShiftComponent,
    CurrentShiftModalComponent,
    RollSplitComponent,
    UnauthorizedComponent,
    KnittingOrderBalanceComponent,
    KnittingOrderFilterComponent,
    WorkOrderSummaryViewerComponent,
    DyeingMachineDashboardComponent,
    DyeingMachineDetailsComponent,
    CardMachineComponent,
    IndividualProductionCardDetailsComponent,
    IndividualProductionCardGeneralInformationComponent,
    ProductionCardBundlesComponent,
    ProductionCardMoveToBatchComponent,
    RollsDisplayTableComponent,
    FabricSummaryTableComponent,
    FinishingRouteDetailComponent,
    ReorganizeProcessModalComponent,
    ConsumedItemModalComponent,
    ChangeDyeingMachineModalComponent,
    CloseProductionCardModalComponent,
    ReassignmentModalComponent,
    SecondQualityRollModalComponent,
    AddFromGreigeModalComponent,
    ReturnRollsToGreigeModalComponent,
    FinishingDashboardComponent,
    FinishingDashboardDetailsComponent,
    FinishingCardComponent,
    LaboratoryTestsComponent,
    DashboardProductionCardComponent,
    WorkOrderQuantitiesModalComponent,
    ReprocessCreationComponent,
    RollFinalQcComponent,
    CuadrajeModalComponent,
    BalanceoModalComponent,
    CycleRecipeValuesComponent,
    ReassignRollsComponent,
    ProductionWorkOrdersComponent,
    ProductionWorkOrderDetailsComponent,
    BatchesComponent,
    BatchDetailsComponent,
    BatchDyeingCycleModalComponent,
    ConsumptionVarianceComponent,
    CreateLotItemModalComponent,
    MachineAssignmentComponent,
    MachineAssignmentPreviewModalComponent,
    DyehouseDailyMetricComponent,
    DyehouseDailyMetricPreviewModalComponent,
    ProductionReportingComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    CardSoftwareLibraryModule,
    CommonModule,
    PrimengModule,
    TranslateWithFallbackPipe,
    TranslateLocalizedPipe,
    GuatemalaTimePipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
    Tooltip,
  ],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra Production',
        contactEmail: 'it-support-sierra-textiles@card-software.com',
        redirectUrl: '/',
      },
    },
    provideHttpClient(withInterceptors([authInterceptor, siteInterceptor, errorInterceptor])),
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    MessageService,
    ConfirmationService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
